"use client";

import { isAfter, isBefore } from "date-fns";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, IconName } from "../icons/Icon";
import { useI18n } from "~/locales/client";
import { ColorModeEnum } from "~/utils/generalUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import Flex from "../flex/Flex";
import Text from "../texts/Text";

type DatePickerProps = {
  firstAvailableDate?: Date;
  lastAvailableDate?: Date;
  selectedDate?: Date;
  onChange?: (date: Date) => void;
  allowPastDates?: boolean;
};

const generateDays = (year: number, month: number) => {
  const days: Array<Date | null> = [];
  const date = new Date(year, month, 1);

  // Push days from the previous month
  const firstDayOfWeek = (date.getDay() || 7) - 1; // Adjust for ISO (Monday = 0)
  if (firstDayOfWeek > 0) {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonthDays = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = firstDayOfWeek; i > 0; i--) {
      days.push(new Date(prevYear, prevMonth, prevMonthDays - i + 1));
    }
  }

  // Add actual days of the current month
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  // Push days from the next month
  const lastDayOfWeek = days.length % 7 === 0 ? 0 : 7 - (days.length % 7);
  if (lastDayOfWeek > 0) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;

    for (let i = 1; i <= lastDayOfWeek; i++) {
      days.push(new Date(nextYear, nextMonth, i));
    }
  }

  return days;
};

// Helper function to get the month name
const getMonthName = (monthIndex: number): string => {
  const monthNames = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  return monthNames[monthIndex];
};

const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const getDateButtonStyles = (
  date: Date | null,
  hoveredDate: Date | null,
  selectedDate: Date | null,
  currentMonth: number,
  firstAvailableDate?: Date,
  lastAvailableDate?: Date,
  minSelectableDate?: Date
) => {
  const baseStyles =
    "p-2 text-center rounded-md text-sm transition-colors ease-in-out duration-200 font-medium cursor-pointer";

  const hoverEffect = date && hoveredDate && date.toDateString() === hoveredDate.toDateString() && "bg-red-100";

  const selectedDateStyles =
    date && selectedDate?.toDateString() === date?.toDateString()
      ? "bg-red-500 text-white hover:bg-red-600"
      : "text-gray-900 hover:bg-red-100";

  const isPastOrBeforeMin =
    date && minSelectableDate && isBefore(date, minSelectableDate);
  const daysBeforeTodayStyles = isPastOrBeforeMin && "text-gray-300 hover:bg-transparent bg-transparent cursor-not-allowed";

  const disabledStyles =
    !date ||
    date.getDay() === 0 ||
    date.getDay() === 6 ||
    isPastOrBeforeMin ||
    (firstAvailableDate && isBefore(date, firstAvailableDate)) ||
    (lastAvailableDate && isAfter(date, lastAvailableDate))
      ? "bg-transparent text-gray-300 cursor-not-allowed hover:bg-transparent"
      : "";

  const otherMonthStyles = date && date.getMonth() !== currentMonth ? "text-gray-400" : "";

  return twMerge(baseStyles, hoverEffect, selectedDateStyles, daysBeforeTodayStyles, disabledStyles, otherMonthStyles);
};

/**
 * A functional component that renders a date picker calendar. It allows the user to select a single date.
 * @param firstAvailableDate - The first available date to be selected
 * @param lastAvailableDate - The last available date to be selected
 * @param selectedDate - The currently selected date
 * @param onChange - Callback function when a date is selected
 * @return {JSX.Element} The date picker component
 */
const DatePicker = ({ firstAvailableDate, lastAvailableDate, selectedDate, onChange, allowPastDates }: DatePickerProps) => {
  const translate = useI18n();
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | null>(selectedDate || null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const effectiveMinDate = allowPastDates
    ? undefined
    : firstAvailableDate && !isBefore(firstAvailableDate, startOfToday)
      ? firstAvailableDate
      : startOfToday;

  const initialDateObj = selectedDate && !isNaN(new Date(selectedDate).getTime()) ? new Date(selectedDate) : startOfToday;

  const [currentYear, setCurrentYear] = useState(initialDateObj.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDateObj.getMonth());
  const daysInMonth = generateDays(currentYear, currentMonth);

  const initialMonth = new Date().getMonth();
  const initialYear = new Date().getFullYear();

  // Handles when the user selects a date (reject weekends, past dates, and outside first/last available)
  const handleDateSelect = (date: Date) => {
    if (
      date.getDay() === 0 ||
      date.getDay() === 6 ||
      (effectiveMinDate && isBefore(date, effectiveMinDate)) ||
      (lastAvailableDate && isAfter(date, lastAvailableDate))
    ) {
      return;
    }
    setInternalSelectedDate(date);
    onChange?.(date);
  };

  return (
    <Flex
      className="bg-white border border-gray-100 rounded-lg mt-2 p-4 w-full flex-col shadow-md"
      data-testid="date-picker"
    >
      {/* Calendar Header: month and year */}
      <Flex className="justify-between items-center mb-4">
        <Icon
          name={IconName.ChevronLeft}
          size={spacing[6]}
          color={!allowPastDates && currentMonth === initialMonth && currentYear === initialYear ? colors.gray[300] : colors.gray[900]}
          colorMode={ColorModeEnum.Fill}
          className={twMerge("p-1 cursor-pointer hover:bg-gray-100 rounded-md")}
          data-testid="icon-chevron-left"
          onClick={() => {
            if (!allowPastDates && currentMonth === initialMonth && currentYear === initialYear) {
              return;
            }
            if (currentMonth === 0) {
              setCurrentMonth(11);
              setCurrentYear(currentYear - 1);
            } else {
              setCurrentMonth(currentMonth - 1);
            }
          }}
        />
        <div className="font-medium">
          {translate(`months.${getMonthName(currentMonth)}`)} {currentYear}
        </div>

        <Icon
          name={IconName.ChevronRight}
          size={spacing[6]}
          colorMode={ColorModeEnum.Fill}
          onClick={() => {
            if (currentMonth === 11) {
              setCurrentMonth(0);
              setCurrentYear(currentYear + 1);
            } else {
              setCurrentMonth(currentMonth + 1);
            }
          }}
          className="p-1 cursor-pointer hover:bg-gray-100 rounded-md"
          data-testid="icon-chevron-right"
        />
      </Flex>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-2 w-full">
        {days.map((day, index) => (
          <Text color="lightGray" key={index} className="text-center text-sm font-medium">
            {translate(`days.${day}`).slice(0, 3).toUpperCase()}
          </Text>
        ))}

        {daysInMonth.map((date: Date | null, index) => (
          <button
            key={index}
            type="button"
            onClick={() => date && handleDateSelect(date)}
            onMouseEnter={() => date && setHoveredDate(date)}
            onMouseLeave={() => setHoveredDate(null)}
            className={twMerge(
              getDateButtonStyles(
                date,
                hoveredDate,
                internalSelectedDate,
                currentMonth,
                firstAvailableDate,
                lastAvailableDate,
                effectiveMinDate
              )
            )}
            disabled={
              !date ||
              date.getDay() === 0 ||
              date.getDay() === 6 ||
              (effectiveMinDate && date ? isBefore(date, effectiveMinDate) : false) ||
              (lastAvailableDate && date ? isAfter(date, lastAvailableDate) : false)
            }
          >
            {date ? date.getDate() : ""}
          </button>
        ))}
      </div>
    </Flex>
  );
};

export default DatePicker;
